import { useState, useEffect, useCallback } from 'react';

import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import DestinationModal from './destination-modal';
import {
  DESTINATION_TYPE_OPTIONS,
  getDefaultDestinationOption,
  getDestinationDisplayNameMapping,
} from './destination-type-options';
import { dedupeDestinationsByCredentials } from './dedupe-destinations';
import { GetDestinations } from '@/connection/greenroom/destinations';
import { DestinationTypeEnum } from '@/types/greenroom/destination.types';
import type { Destination } from '@/types/greenroom/destination.types';
import { useAppSelector } from '@/store/hooks';
import { selectGreenroomMeeting } from '@/store/slices/project/selectors';
import { selectUserChannel } from '@/store/slices/user/selectors';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

type DestinationSelectProps = {
  disabled?: boolean;
};

const DestinationSelect = ({ disabled }: DestinationSelectProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [initialExpandedId, setInitialExpandedId] = useState<number | string | null>(null);
  const [savedDestinations, setSavedDestinations] = useState<Destination[]>([]);
  const [parentFocus, setParentFocus] = useState(false);
  const greenroomMeeting = useAppSelector(selectGreenroomMeeting);
  const channel = useAppSelector(selectUserChannel);
  const roomId = greenroomMeeting?.meeting != null ? String(greenroomMeeting.meeting) : undefined;

  const loadDestinations = useCallback(async () => {
    if (!roomId) return;
    const response = await GetDestinations(roomId);
    if (!response) return;

    setSavedDestinations(dedupeDestinationsByCredentials(response.destinations));
  }, [roomId]);

  useEffect(() => {
    loadDestinations();
  }, [loadDestinations]);

  const handleClose = () => {
    setModalOpen(false);
    setInitialExpandedId(null);
    loadDestinations();
  };

  return (
    <>
      <div
        role="button"
        aria-label="Destination select"
        aria-disabled={disabled}
        tabIndex={0}
        className="outline-none"
        onFocus={() => setParentFocus(true)}
        onBlur={() => setParentFocus(false)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setModalOpen(true);
          }
        }}
      >
        <EditStateWrapper
          Label="DESTINATION"
          className="position-relative cursor-pointer"
          headerClassName="position-absolute width-100-percent"
          disabled={disabled}
          onFocusedWithinChange={(focused) => {
            if (focused) setModalOpen(true);
          }}
        >
          {({ isFocusedWithin }) => (
            <Container
              className="flex ai-center flex-wrap gap-6 margin-t-18 cursor-pointer"
              width="100%"
              borderRadius="100px"
              border={parentFocus ? '1px solid var(--global-ico-text-1)' : '1px solid transparent'}
              height="36px"
              padding="4px 0"
              data-show-edit-label={isFocusedWithin ? 'false' : 'true'}
            >
              {savedDestinations.length === 0 ? (
                <Text className="L2" design="GlobalIcoText3">
                  No destinations
                </Text>
              ) : (
                <>
                  {savedDestinations.slice(0, 2).map((dest) => {
                    // The server may return a synthetic BLP/platform destination with no `type` —
                    // treat it the same as Streams. See destination.types.ts for the full contract.
                    const isPlatformDestination = dest.type == null || dest.type === DestinationTypeEnum.Streams;
                    const typeOpt = isPlatformDestination
                      ? getDefaultDestinationOption(channel?.type)
                      : (DESTINATION_TYPE_OPTIONS.find((o) => o.value === dest.type) ?? DESTINATION_TYPE_OPTIONS[0]);
                    return (
                      <div key={dest.id ?? dest.url} onPointerDown={() => setInitialExpandedId(dest.id ?? dest.url)}>
                        <Container
                          className="flex ai-center gap-6 padding-l-10 padding-r-12 radius-100"
                          border="2px solid var(--local-chip-input-default-outline)"
                          background="var(--local-chip-input-default-bg)"
                          hoverBackground="var(--local-chip-input-hover-bg)"
                          maxWidth="150px"
                          minWidth="0"
                          height="28px"
                        >
                          <Container className="flex flex-shrink-0">
                            <Icon name={typeOpt.icon} size={12} color={getThemeColorCSSVariable()} />
                          </Container>
                          <Text className="S3" design="GlobalIcoText1" clamp={1}>
                            {getDestinationDisplayNameMapping(dest.name, typeOpt.label)}
                          </Text>
                        </Container>
                      </div>
                    );
                  })}
                  {savedDestinations.length > 2 && (
                    <Container
                      className="flex ai-center padding-h-12 radius-100"
                      border="2px solid var(--local-chip-input-default-outline)"
                      background="var(--local-chip-input-default-bg)"
                      minWidth="0"
                      height="28px"
                    >
                      <Text className="S3" whiteSpace="nowrap" design="GlobalIcoText1">
                        +{savedDestinations.length - 2}
                      </Text>
                    </Container>
                  )}
                </>
              )}
            </Container>
          )}
        </EditStateWrapper>
      </div>

      <DestinationModal open={modalOpen} onClose={handleClose} initialExpandedId={initialExpandedId} />
    </>
  );
};

export default DestinationSelect;
